import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Sidebar from "components/sidebar";
import Footer from "components/footer/Footer";
import {
  MdAssignment,
  MdBusiness,
  MdCalendarMonth,
  MdClass,
  MdDashboard,
  MdFactCheck,
  MdMenu,
  MdNotifications,
  MdPeople,
} from "react-icons/md";
import systemAdminRoutes from "routes/systemAdminRoutes";
import CenterDetail from "views/systemadmin/centers/detail";
import CenterClassesMgmt from "views/systemadmin/centers/management/CenterClassesMgmt";
import CenterSessionsMgmt from "views/systemadmin/centers/management/CenterSessionsMgmt";
import CenterUsersMgmt from "views/systemadmin/centers/management/CenterUsersMgmt";
import CenterStaffAttendance from "views/systemadmin/centers/management/CenterStaffAttendance";
import CenterSessionReports from "views/systemadmin/centers/management/CenterSessionReports";
import CenterSendNotifications from "views/systemadmin/centers/management/CenterSendNotifications";
import CenterSettingsView from "views/systemadmin/centers/management/CenterSettingsView";
import { centerService } from "services/admin/centerService";
import NotificationsPage from "views/notifications";
import UserProfileReadonly from "views/admin/users-management/UserProfileReadonly";
import { useLanguage } from "context/LanguageContext";

const SELECTED_CENTER_KEY = "systemadmin.selectedCenter";

/** Extract centerId from paths like /systemadmin/centers/:id[/...] */
function extractCenterId(pathname: string): string | null {
  const match = pathname.match(/\/systemadmin\/centers\/([^/]+)/);
  return match ? match[1] : null;
}

export default function SystemAdminLayout() {
  const { t } = useLanguage();
  const [open, setOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);
  const [centerName, setCenterName] = React.useState<string | null>(null);
  const [centerLoading, setCenterLoading] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1200) setOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prefer the centerId encoded in the URL; if absent (e.g. user clicked a
  // top-level item like "Quản lý người dùng" while still acting on a center),
  // fall back to the last selected center so the sub-sidebar keeps showing
  // context. This avoids the sub-sidebar collapsing mid-flow.
  const centerId = React.useMemo(() => {
    const fromPath = extractCenterId(location.pathname);
    if (fromPath) {
      sessionStorage.setItem(SELECTED_CENTER_KEY, fromPath);
      return fromPath;
    }
    return sessionStorage.getItem(SELECTED_CENTER_KEY);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!centerId) {
      setCenterName(null);
      return;
    }
    setCenterLoading(true);
    centerService
      .getById(centerId)
      .then((center) => setCenterName(center.name ?? null))
      .catch(() => setCenterName(null))
      .finally(() => setCenterLoading(false));
  }, [centerId]);

  /** Inject center sub-nav into the centers route when inside a center */
  const activeRoutes: RoutesType[] = React.useMemo(() => {
    if (!centerId) return systemAdminRoutes;
    return systemAdminRoutes.map((r) => {
      if (r.path === "centers") {
        return {
          ...r,
          contextLabel: centerLoading
            ? t("sysLayout.loadingCenter")
            : centerName ?? undefined,
          children: [
            {
              name: "sysLayout.centerOverview",
              icon: <MdDashboard className="h-4 w-4" />,
              href: `/systemadmin/centers/${centerId}`,
            },
            {
              name: "sysLayout.centerClasses",
              icon: <MdClass className="h-4 w-4" />,
              href: `/systemadmin/centers/${centerId}/classes`,
            },
            {
              name: "sysLayout.centerSessions",
              icon: <MdCalendarMonth className="h-4 w-4" />,
              href: `/systemadmin/centers/${centerId}/sessions`,
            },
            {
              name: "sysLayout.centerUsers",
              icon: <MdPeople className="h-4 w-4" />,
              href: `/systemadmin/centers/${centerId}/users`,
            },
            {
              name: "nav.staffAttendance",
              icon: <MdFactCheck className="h-4 w-4" />,
              href: `/systemadmin/centers/${centerId}/staff-attendance`,
            },
            {
              name: "nav.reportTemplates",
              icon: <MdAssignment className="h-4 w-4" />,
              href: `/systemadmin/centers/${centerId}/report-templates`,
            },
            {
              name: "nav.sendNotification",
              icon: <MdNotifications className="h-4 w-4" />,
              href: `/systemadmin/centers/${centerId}/send-notifications`,
            },
            {
              name: "nav.centerSettings",
              icon: <MdBusiness className="h-4 w-4" />,
              href: `/systemadmin/centers/${centerId}/settings`,
            },
          ],
        };
      }
      return r;
    });
  }, [centerId, centerLoading, centerName, t]);

  const getRoutes = (routes: RoutesType[]) =>
    routes.map((prop, key) =>
      prop.layout === "/systemadmin" ? (
        <Route path={`/${prop.path}`} element={prop.component} key={key} />
      ) : null
    );

  document.documentElement.dir = "ltr";
  return (
    <div className="flex h-full w-full">
      <Sidebar
        open={open}
        collapsed={collapsed}
        onClose={() => setOpen(false)}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        routes={activeRoutes}
      />
      <div className="h-full w-full bg-canvas">
        <main
          className={`mx-[12px] h-full flex-none transition-all duration-300 md:pr-2 ${
            collapsed ? "xl:ml-[92px]" : "xl:ml-[292px]"
          }`}
        >
          <div className="flex h-full flex-col">
            <div className="flex items-center px-2 pt-4 xl:hidden">
              <button
                onClick={() => setOpen(true)}
                className="flex h-10 w-10 items-center justify-center rounded-lg text-ink transition-colors hover:bg-surface-hover"
              >
                <MdMenu className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 p-2 md:pr-2">
              <Routes>
                {getRoutes(systemAdminRoutes)}
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route
                  path="/users-management/:userId/profile"
                  element={<UserProfileReadonly />}
                />
                <Route path="/centers/:centerId" element={<CenterDetail />} />
                <Route
                  path="/centers/:centerId/edit"
                  element={<CenterDetail />}
                />
                <Route
                  path="/centers/:centerId/classes/*"
                  element={<CenterClassesMgmt />}
                />
                <Route
                  path="/centers/:centerId/sessions"
                  element={<CenterSessionsMgmt />}
                />
                <Route
                  path="/centers/:centerId/users"
                  element={<CenterUsersMgmt />}
                />
                <Route
                  path="/centers/:centerId/staff-attendance"
                  element={<CenterStaffAttendance />}
                />
                <Route
                  path="/centers/:centerId/report-templates"
                  element={<CenterSessionReports />}
                />
                <Route
                  path="/centers/:centerId/send-notifications"
                  element={<CenterSendNotifications />}
                />
                <Route
                  path="/centers/:centerId/settings"
                  element={<CenterSettingsView />}
                />
                <Route
                  path="/"
                  element={<Navigate to="/systemadmin/dashboard" replace />}
                />
              </Routes>
            </div>
            <Footer />
          </div>
        </main>
      </div>
    </div>
  );
}
